import { ShopifyProduct, Store } from "../../../../../../common/models";
import { FloatingMenuButtonPopoverItems } from "../../../../common/FloatingMenuButton";
import { ColumnConfig } from "../../../../common/tables/config/ColumnConfig";
import { EnumResolver } from "../../../../common/tables/etc/EnumResolver";
import {
    TableBodyCellRendererInput,
    TableCellChangedHandler,
    TableCellFactory,
    TableCellHeaderChangedHandler,
} from "../../../../common/tables/factory";
import {
    TableFilterableHeaderCellRenderer
} from "../../../../common/tables/factory";
import { Sort } from "../../../../common/tables/state/TableProviderAPI";
import { SupportedColor } from "../../../../common/tables/etc/StyledSelectMenuItem";
import { ShopifyProductForAdmin } from "../../../../../../common/tables/shopify/ShopifyProductForAdmin";
import {
    FilterExpression,
    FilterExpressionType,
    SortOrder
} from "../../../../../../common/query-filters";
import { ShopifyProductEnumResolver } from "../../etc/ShopifyProductEnumResolver";
import { queryShopifyProductsForAdmin } from "../../../../datastore/ShopifyProductDataStore";
import { queryStoresForAdmin } from "../../../../datastore/StoreDataStore";
import { StoreForAdmin } from "../../../../../../common/tables/store/StoreForAdmin";
import {
    TableSearchableSingleSelectCellRenderer
} from "../../../../common/tables/factory/renderers/TableSearchableSingleSelectCellRenderer";

type RendererInput = TableBodyCellRendererInput<ShopifyProductForAdmin>;

export class ShopifyProductTableCellFactory extends TableCellFactory<ShopifyProductForAdmin, ShopifyProduct> {

    readonly enumResolver: EnumResolver<ShopifyProductForAdmin> = new ShopifyProductEnumResolver();

    public renderHeaderCell(
        columnConfig: ColumnConfig<ShopifyProductForAdmin>,
        filterExpressions: FilterExpression<ShopifyProductForAdmin>[],
        sort: Sort<ShopifyProductForAdmin>,
        onCellChanged: TableCellHeaderChangedHandler<ShopifyProductForAdmin>,
    ): JSX.Element {
        if (columnConfig.columnName === "id") {
            return new TableFilterableHeaderCellRenderer<
                ShopifyProductForAdmin, ShopifyProductForAdmin, ShopifyProduct
            >(
                {columnConfig},
                filterExpressions,
                sort,
                onCellChanged,
                [],
                {
                    query: (input) => {
                        // Only fire the lookup once the user has typed/pasted
                        // something — avoids an empty "id IN ()" query.
                        const hasValues = (input.expressions ?? []).some(
                            e => e.columnValue?.length > 0 && e.columnValue.some(v => v !== "")
                        );
                        if (!hasValues) {
                            return Promise.resolve([]);
                        }
                        return queryShopifyProductsForAdmin(input);
                    },
                    queryInput: {
                        expressions: [{
                            columnName: "id",
                            columnValue: [""],
                            type: FilterExpressionType.equals,
                            caseInsensitive: false,
                        }],
                        page: 0,
                        pageSize: 8,
                        sortOrder: SortOrder.ASCENDING,
                        sortColumn: "id"
                    },
                    resultsMap: (product: ShopifyProduct) => ({
                        label: String(product.id),
                        value: String(product.id),
                        isVerified: false,
                    }),
                    placeholderText: "Enter a full Shopify Product ID",
                }
            ).render();
        }

        if (columnConfig.columnName === "mainSku") {
            return new TableFilterableHeaderCellRenderer<
                ShopifyProductForAdmin, ShopifyProductForAdmin, ShopifyProduct
            >(
                {columnConfig},
                filterExpressions,
                sort,
                onCellChanged,
                [],
                {
                    query: (input) => {
                        // MenuContent's useEffect resolves initial selections by
                        // querying with columnName "id". Remap to "mainSku" so
                        // the server filters on the related ProductListing field.
                        const expressions = (input.expressions ?? []).map(e =>
                            (e.columnName as string) === "id"
                                ? { ...e, columnName: "mainSku" as keyof ShopifyProductForAdmin }
                                : e
                        );
                        const hasValues = expressions.some(e => e.columnValue?.length > 0);
                        if (!hasValues) {
                            return Promise.resolve([]);
                        }
                        return queryShopifyProductsForAdmin({ ...input, expressions });
                    },
                    queryInput: {
                        expressions: [{
                            columnName: "mainSku",
                            columnValue: [""],
                            type: FilterExpressionType.contains,
                            caseInsensitive: true,
                        }],
                        page: 0,
                        pageSize: 8,
                        sortOrder: SortOrder.ASCENDING,
                        sortColumn: "mainSku"
                    },
                    resultsMap: (product: ShopifyProduct) => {
                        const mainSku = (product as any).productListing?.mainSku ?? "";
                        return {
                            label: mainSku,
                            value: mainSku,
                            isVerified: false,
                        };
                    }
                }
            ).render();
        }

        if (columnConfig.columnName === "title") {
            return new TableFilterableHeaderCellRenderer<
                ShopifyProductForAdmin, ShopifyProductForAdmin, ShopifyProduct
            >(
                {columnConfig},
                filterExpressions,
                sort,
                onCellChanged,
                [],
                {
                    query: (input) => {
                        // MenuContent's useEffect resolves initial selections by
                        // querying with columnName "id". ShopifyProduct.id is BigInt
                        // and our filter values are titles, not IDs — remap to "title".
                        const expressions = (input.expressions ?? []).map(e =>
                            (e.columnName as string) === "id"
                                ? { ...e, columnName: "title" as keyof ShopifyProductForAdmin }
                                : e
                        );
                        const hasValues = expressions.some(e => e.columnValue?.length > 0);
                        if (!hasValues) {
                            return Promise.resolve([]);
                        }
                        return queryShopifyProductsForAdmin({ ...input, expressions });
                    },
                    queryInput: {
                        expressions: [{
                            columnName: "title",
                            columnValue: [""],
                            type: FilterExpressionType.contains,
                            caseInsensitive: true,
                        }],
                        page: 0,
                        pageSize: 8,
                        sortOrder: SortOrder.ASCENDING,
                        sortColumn: "title"
                    },
                    resultsMap: (product: ShopifyProduct) => ({
                        label: product.title ?? "",
                        value: product.title ?? "",
                        isVerified: false,
                    })
                }
            ).render();
        }

        if (columnConfig.columnName === "storeName") {
            return new TableFilterableHeaderCellRenderer<
                ShopifyProductForAdmin, StoreForAdmin, Store
            >(
                {columnConfig},
                filterExpressions,
                sort,
                onCellChanged,
                [],
                {
                    query: queryStoresForAdmin,
                    queryInput: {
                        expressions: [{
                            columnName: "name",
                            columnValue: [""],
                            type: FilterExpressionType.contains,
                            caseInsensitive: true,
                        }],
                        page: 0,
                        pageSize: 8,
                        sortOrder: SortOrder.ASCENDING,
                        sortColumn: "name"
                    },
                    resultsMap: (store: Store) => ({
                        label: store.name,
                        value: store.id,
                        isVerified: !!store.internalVendorName,
                    })
                }
            ).render();
        }

        return super.renderHeaderCell(columnConfig, filterExpressions, sort, onCellChanged);
    }

    public renderBodyCell(
        columnConfig: ColumnConfig<ShopifyProductForAdmin>,
        rowData: ShopifyProduct,
        onCellChanged: TableCellChangedHandler<ShopifyProductForAdmin>,
    ): JSX.Element {
        if (columnConfig.columnName === "storeName") {
            const rendererInput: RendererInput = {
                columnConfig: columnConfig,
                cellValue: this.resolveCellData(columnConfig, rowData),
                id: rowData.id,
                onCellChanged: onCellChanged
            };
            return new TableSearchableSingleSelectCellRenderer<ShopifyProductForAdmin, StoreForAdmin, Store>(
                rendererInput,
                {
                    query: queryStoresForAdmin,
                    queryInput: {
                        expressions: [{
                            columnName: "name",
                            columnValue: [""],
                            type: FilterExpressionType.contains,
                            caseInsensitive: true,
                        }],
                        page: 0,
                        pageSize: 20,
                        sortOrder: SortOrder.ASCENDING,
                        sortColumn: "internalVendorName"
                    },
                    resultsMap: (store: Store) => ({
                        label: store.name,
                        value: store.id,
                        isVerified: !!store.internalVendorName,
                    }),
                    placeholderText: "Search for store name..."
                },
            ).render();
        }

        return super.renderBodyCell(columnConfig, rowData, onCellChanged);
    }

    protected resolveCellBadgeColor(
        columnConfig: ColumnConfig<ShopifyProductForAdmin>,
        _shopifyProduct: ShopifyProduct,
    ): SupportedColor {
        if (columnConfig.columnName === "storeName") {
            return;
        }

        return super.resolveCellBadgeColor(columnConfig, _shopifyProduct);
    }

    protected resolveCellData(
        columnConfig: ColumnConfig<ShopifyProductForAdmin>,
        shopifyProduct: ShopifyProduct,
    ): string {
        const data = shopifyProduct as any;

        if (columnConfig.columnName === "storeName") {
            return data.store?.name ?? "";
        }

        if (columnConfig.columnName === "productListingId") {
            return data.productListing?.id ?? "";
        }

        if (columnConfig.columnName === "productListingName") {
            return data.productListing?.name ?? "";
        }

        if (columnConfig.columnName === "productListingStatus") {
            return data.productListing?.status ?? "";
        }

        if (columnConfig.columnName === "mainSku") {
            return data.productListing?.mainSku ?? "";
        }

        if (columnConfig.columnName === "variantCount") {
            return (data.variants?.length ?? 0).toString();
        }

        // TODO: recategorize the `hasPayoutRuleAssignments` and `hasCostMetadata`
        // cells to use a new `TableAssignableRelationCellRenderer`. The cell should
        // render "n assigned" (or blank when n=0); on hover it should show
        // "Click to assign", and clicking should open a new tab deeplinked to the
        // corresponding Product Details > Payout Rules or Cost Metadata tab for
        // this product listing.
        if (columnConfig.columnName === "hasPayoutRuleAssignments") {
            return data.hasPayoutRuleAssignments ? "Yes" : "No";
        }

        if (columnConfig.columnName === "hasCostMetadata") {
            return data.hasCostMetadata ? "Yes" : "No";
        }

        return super.resolveCellData(columnConfig, shopifyProduct);
    }

    protected resolvePopoverCellMenuItems(rowData: ShopifyProduct): FloatingMenuButtonPopoverItems[] {
        const productListing = (rowData as any).productListing;
        const plId = productListing?.id;
        return [
            {
                label: "Product Variants",
                onSelect: () => {
                    if (plId) {
                        this.openUrl(`/products/${plId}/product-variants`);
                    }
                },
                disabled: !plId,
            },
            {
                label: "Cost Metadata",
                onSelect: () => {
                    if (plId) {
                        this.openUrl(`/products/${plId}/cost-metadata`);
                    }
                },
                disabled: !plId,
            },
            {
                label: "Payout Rules",
                onSelect: () => {
                    if (plId) {
                        this.openUrl(`/products/${plId}/payout-rules`);
                    }
                },
                disabled: !plId,
            },
        ];
    }

    /**
     * Returns an array of warning messages for the given row.
     * A row has warnings if any of: storeId is null, productListingId is null,
     * hasPayoutRuleAssignments is false, or hasCostMetadata is false.
     */
    public getRowWarnings(rowData: ShopifyProduct): string[] {
        const data = rowData as any;
        const warnings: string[] = [];

        if (!data.store?.id) {
            warnings.push("No store assigned");
        }

        if (!data.productListing?.id) {
            warnings.push("No product listing");
        }

        if (!data.hasPayoutRuleAssignments) {
            warnings.push("No payout rules assigned");
        }

        if (!data.hasCostMetadata) {
            warnings.push("No cost metadata assigned");
        }

        return warnings;
    }

    /**
     * Returns true if the row should be styled with the warning class.
     */
    public hasRowWarning(rowData: ShopifyProduct): boolean {
        const data = rowData as any;
        return !data.store?.id
            || !data.productListing?.id
            || !data.hasPayoutRuleAssignments
            || !data.hasCostMetadata;
    }

    public getRowClassName(rowData: ShopifyProduct): string {
        return this.hasRowWarning(rowData) ? "jc-row-warning" : "";
    }
}
